/**
* 存管开户模块
*开户模块的梳理（说明：开户模块需要在登录之后才能操作）
* accountType： 存管账户(默认)、基础账户、卡牛账户
* forwardUrl：开户完成后的返回地址，默认location.href
* openInCurrentView: 是否在当前webview打开开户链接（客户端有用）
* isShowAccountCustodyDialog: 检测没有开通存管账户时，是否显示弹窗，不显示则直接跳转到开户页面
* success: 若检测用户已开户的回调函数
> 说明： 如果在检测开户接口中遇到报错，直接提示出来即可
*/

import domain from '@common/domain'
import toast from '@common/toast'
import ajax from '@finance/ajax'
import dialog from './mixin/dialog'

import { callbackHandler, getUserData } from '../utils'

class AccountCustodyAuth {
  constructor(options) {
    this.options = options
    this.configs = {
      stock: {
        title: '激活银行存管账户',
        content: '本平台已接入广发银行资金存管系统，充值、投资、提现等资金操作均需开通存管账户',
        buttonText: '激活存管账户',
        url: `${domain.res}/public-vue/fixin-account/index.html#/open?origin=${this.options.origin}&forward=${encodeURIComponent(this.options.forwardUrl)}`,
        handleConfirm: () => {
          window.location.href = this.configs.stock.url
        }
      },
      new: {
        title: '开通银行存管账户',
        content: '本平台已接入广发银行资金存管系统，充值、投资、提现等资金操作均需开通存管账户',
        buttonText: '开通存管账户',
        url: `${domain.res}/public-vue/fixin-account/index.html#/open?origin=${this.options.origin}&forward=${encodeURIComponent(this.options.forwardUrl)}`,
        handleConfirm: () => {
          window.location.href = this.configs.new.url
        }
      },
      accredit: {
        title: '授权操作',
        content: '开户成功，您还需要授权后才能进行投资',
        buttonText: '去授权',
        url: `${domain.res}/public-vue/fixin-account/index.html#/open?origin=${this.options.origin}&forward=${encodeURIComponent(this.options.forwardUrl)}`,
        handleConfirm: () => {
          window.location.href = this.configs.accredit.url
        }
      },
      locked: {
        title: '解锁银行存管账户',
        content: '您多次输错密码导致银行存管账户锁定，继续操作需解锁账户',
        buttonText: '去解锁',
        url: `${domain.res}/public-vue/fixin-account/index.html#/open?origin=${this.options.origin}&forward=${encodeURIComponent(this.options.forwardUrl)}`,
        requestApiLocked: false, // 请求接口防重发
        handleConfirm: () => {
          // 需要去请求解锁接口
          if (!this.configs.locked.requestApiLocked) {
            this.configs.locked.requestApiLocked = true
            ajax.request({
              url: `${domain.api}/custodyws/ws/fixin/custody/investor/unLock`,
              body: {
                backUrl: window.location.href
              }
            }).then((resultData) => {
              this.configs.locked.requestApiLocked = false
              if (resultData.succeed) {
                window.location.href = resultData.data.url
              } else {
                toast(resultData.msg)
              }
            }).catch(() => {
              this.configs.locked.requestApiLocked = false
            })
          }
        }
      },
      wait: {
        title: '审核中',
        content: '您提交的资料正在审核中，请等待开户成功后再进行充值、提现和购买等操作，开户成功后我们会以短信的方式通知您',
        buttonText: '我知道了',
        url: `${domain.res}/public-vue/fixin-account/index.html#/open?origin=${this.options.origin}&forward=${encodeURIComponent(this.options.forwardUrl)}`,
        handleConfirm: () => {}
      }
    }
  }
  // 配置
  // 开户来源的配置有问题
  static configs = {}
  /**
 * 参数说明：
 * registerStatus：开户状态【1:正常, 2:待处理, 3:确认中, 4:锁定, 5:失败】
 * accreditStatus：授权状态【0:未授权, 1:已授权】
 * stockStatus：存量用户标识【0:存量未激活用户, 1:非存量用户】
 */
  static userStatus(stockStatus, registerStatus, accreditStatus) {
    // 【stock:存量, pass:已开户, accredit:待授权, locked:待解锁, wait:待确认, new:新用户】
    if (registerStatus === 3) {
      return 'wait'
    } else if (registerStatus === 4) {
      return 'locked'
    } else if (stockStatus === 0) {
      return 'stock'
    } else if (registerStatus === 1 && accreditStatus === 1) {
      return 'pass'
    } else if (registerStatus === 1 && accreditStatus === 0) {
      return 'accredit'
    }
    return 'new'
  }
  // 成功回调
  async successCallback() {
    try {
      const userData = await getUserData(this.options.isNeedUserData)
      callbackHandler(this.options.success, userData)
    } catch (error) {
      console.log('Get UserData Error:', error)
    }
  }
  // 渲染
  render(state) {
    if (state === 'pass') {
      this.successCallback()
    } else if (state === 'wait' || state === 'locked') {
      // 如果审核中，锁定中不弹窗弹窗，会引起跳转问题
      dialog({
        title: this.configs[state].title,
        content: this.configs[state].content,
        buttonText: this.configs[state].buttonText,
        handleConfirm: this.configs[state].handleConfirm
      })
    } else {
      window.location.href = this.configs[state].url
    }
  }
  // 执行
  async init() {
    try {
      const result = await ajax.request({ url: `${domain.api}/custodyws/ws/fixin/custody/investor/queryStatus` })
      if (result.succeed) {
        this.render(AccountCustodyAuth.userStatus(result.data.stockStatus, result.data.registerStatus, result.data.accreditStatus))
      } else if (result.code === '100004') {
        // 返回码100004表示当前用户id还未在存管账户表中，是新用户
        this.render('new')
      } else {
        toast(result.msg)
      }
    } catch (error) {
      toast('网络请求失败，请稍后再试')
      console.error('RequestApi Error: queryStatus', error)
    }
  }
}

export const accountCustody = (options) => {
  const accountCustodyAuth = new AccountCustodyAuth(options)
  accountCustodyAuth.init()
}

export default {
  accountCustody
}



// WEBPACK FOOTER //
// ./node_modules/@finance/login/src/account/account_custody.js